import { useState } from 'react';
import { Form, Input, Button, Alert } from 'antd';
import {
  LockOutlined,
  MailOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useInternalAdminAuth from '../../contexts/InternalAdminAuthContext';
import { useAppNotifications } from '../../utils/notify';

// Stage 5N-2B: Glass-i internal-admin login page.
//
// Visually distinct from /login so an operator can never accidentally
// type customer credentials here and vice versa:
//   - Dark slate header chip with shield icon
//   - "Glass-i Internal Admin" heading
//   - Authorized-only warning chip
//
// Token isolation: this page calls internalAuth.login() which stores
// tokens via setInternalTokens() in internalAdminClient.js. The tenant
// AuthContext is untouched.

export default function InternalAdminLoginPage() {
  const { login } = useInternalAdminAuth();
  const navigate = useNavigate();
  const { notifyError } = useAppNotifications();
  const [submitting, setSubmitting] = useState(false);

  async function onFinish(values) {
    setSubmitting(true);
    try {
      const email = (values.email || '').trim().toLowerCase();
      await login({ email, password: values.password });
      navigate('/internal/accounts/pending', { replace: true });
    } catch (err) {
      notifyError(err, { title: 'Sign in failed' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-slate-100">
      <div className="w-full max-w-md">
        {/* Internal badge — sits above the card so it reads as a frame
            label rather than a brand mark. */}
        <div className="mb-4 flex items-center justify-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold tracking-wide uppercase shadow-sm">
            <SafetyCertificateOutlined />
            Internal access
          </span>
        </div>

        <div className="bg-white rounded-2xl shadow-elevated border border-surface-200 p-6 sm:p-8">
          <div className="mb-5">
            <h1 className="text-2xl font-bold text-ink-900 m-0 leading-tight tracking-tight">
              Glass-i Internal Admin
            </h1>
            <p className="text-sm text-ink-500 m-0 mt-1.5">
              Sign in to review accounts and manage approvals.
            </p>
          </div>

          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message="Authorized Glass-i team members only."
            description="Customer accounts cannot sign in here. If you reached this page in error, return to the customer login."
          />

          <Form
            layout="vertical"
            onFinish={onFinish}
            disabled={submitting}
            size="large"
            className="onboarding-compact-form"
          >
            <Form.Item
              label={<span className="text-sm font-semibold text-ink-800">Email</span>}
              name="email"
              rules={[
                { required: true, message: 'email is required' },
                { type: 'email', message: 'enter a valid email address' }
              ]}
            >
              <Input
                prefix={<MailOutlined className="text-ink-400" />}
                placeholder="ops@glass-i.com"
                autoComplete="username"
                inputMode="email"
              />
            </Form.Item>

            <Form.Item
              label={<span className="text-sm font-semibold text-ink-800">Password</span>}
              name="password"
              rules={[{ required: true, message: 'password is required' }]}
            >
              <Input.Password
                prefix={<LockOutlined className="text-ink-400" />}
                autoComplete="current-password"
                placeholder="Your password"
              />
            </Form.Item>

            <Form.Item className="!mb-0 !mt-1">
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                block
                className="!h-12 !text-base !font-semibold"
              >
                Sign in
              </Button>
            </Form.Item>
          </Form>

          <p className="text-xs text-ink-400 m-0 mt-4 text-center">
            Customer login is at{' '}
            <a className="font-medium text-brand-500 hover:text-brand-600" href="/login">
              /login
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
